import {
  createContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import type { Tenant, TenantSettings } from "@/types/tenant";
import { TenantService } from "@/services/tenantService";
import { SupabaseImageService } from "@/services/supabaseImageService";

interface TenantContextProps {
  tenant: Tenant | null;
  tenantSettings: TenantSettings | null;
  tenantId: string | null;
  tenantSlug: string | null;
  loading: boolean;
  error: string | null;
  /** Whether a tenant has been resolved */
  isResolved: boolean;
  /** Whether the tenant uses a dedicated Supabase database */
  hasDedicatedDb: boolean;
  /** Refresh tenant data */
  refreshTenant: () => Promise<void>;
  /** Check if a feature is enabled for this tenant */
  isFeatureEnabled: (feature: string) => boolean;
}

const TenantContext = createContext<TenantContextProps | undefined>(undefined);

export { TenantContext };

/**
 * Extracts the tenant slug from the current hostname.
 * E.g., "acme.taskrsol.com" → "acme"
 * Returns null for localhost, plain domains, etc.
 */
function extractSlugFromHostname(): string | null {
  const hostname = window.location.hostname;

  // Skip localhost and IP addresses
  if (hostname === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    // In dev, check for a VITE_TENANT_SLUG override
    const envSlug = import.meta.env.VITE_TENANT_SLUG;
    return envSlug || null;
  }

  const parts = hostname.split(".");

  // Need at least 3 parts: subdomain.domain.tld
  if (parts.length >= 3) {
    const subdomain = parts[0];
    // Skip common subdomains that aren't tenant slugs
    if (["www", "app", "api", "dev", "staging"].includes(subdomain)) {
      return null;
    }
    return subdomain;
  }

  return null;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasDedicatedDb, setHasDedicatedDb] = useState(false);

  const tenantId = tenant?.id || null;
  const tenantSlug = tenant?.slug || null;
  const isResolved = !!tenant;

  const resolveTenant = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const slug = extractSlugFromHostname();

      const applyResolved = (resolved: Tenant) => {
        setTenant(resolved);
        const settings = Array.isArray(resolved.tenant_settings)
          ? resolved.tenant_settings[0]
          : resolved.tenant_settings;
        setTenantSettings(settings || null);

        // Check if tenant has a dedicated DB — non-blocking (don't delay login)
        const token = localStorage.getItem("taskr_access_token");
        if (token) {
          TenantService.hasDedicatedDb(resolved.id)
            .then((dedicated) => setHasDedicatedDb(dedicated))
            .catch(() => setHasDedicatedDb(false));
        }
      };

      if (slug) {
        const resolved = await TenantService.resolveTenantBySlug(slug);
        if (resolved) {
          applyResolved(resolved);
          return;
        }
      }

      // Try custom domain resolution
      const hostname = window.location.hostname;
      if (hostname !== "localhost" && !/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
        const resolved = await TenantService.resolveTenantByDomain(hostname);
        if (resolved) {
          applyResolved(resolved);
          return;
        }
      }

      // No tenant resolved - this is okay for super admin or dev mode
      setTenant(null);
      setTenantSettings(null);
      setHasDedicatedDb(false);
    } catch (err) {
      console.error("Error resolving tenant:", err);
      setError("Failed to resolve tenant");
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshTenant = useCallback(async () => {
    await resolveTenant();
  }, [resolveTenant]);

  const isFeatureEnabled = useCallback(
    (feature: string): boolean => {
      if (!tenantSettings) return true; // Default to enabled if no tenant
      const key = `feature_${feature}` as keyof TenantSettings;
      const value = tenantSettings[key];
      return typeof value === "boolean" ? value : true;
    },
    [tenantSettings]
  );

  // Resolve tenant on mount
  useEffect(() => {
    resolveTenant();
  }, [resolveTenant]);

  // Apply tenant branding when settings change
  useEffect(() => {
    if (!tenantSettings) return;

    // Update page title
    if (tenantSettings.site_title) {
      document.title = tenantSettings.site_title;
    }

    // Update favicon — resolve storage path to signed URL
    if (tenantSettings.favicon_url) {
      const setFavicon = (href: string) => {
        let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
        if (!link) {
          link = document.createElement("link");
          link.rel = "icon";
          document.head.appendChild(link);
        }
        link.href = href;
      };

      const raw = tenantSettings.favicon_url;
      if (raw.startsWith("http")) {
        // Legacy full URL — extract path if it's a storage URL, otherwise use directly
        if (raw.includes("/storage/v1/object/public/task-files/")) {
          const path = raw.split("/storage/v1/object/public/task-files/")[1];
          SupabaseImageService.getSignedUrl(path).then((url) => {
            if (url) setFavicon(url);
          });
        } else {
          setFavicon(raw);
        }
      } else {
        // Storage path
        SupabaseImageService.getSignedUrl(raw).then((url) => {
          if (url) setFavicon(url);
        });
      }
    }

    // Apply CSS custom properties for tenant colors
    if (tenantSettings.primary_color) {
      document.documentElement.style.setProperty(
        "--tenant-primary-color",
        tenantSettings.primary_color
      );
    }
    if (tenantSettings.accent_color) {
      document.documentElement.style.setProperty(
        "--tenant-accent-color",
        tenantSettings.accent_color
      );
    }
  }, [tenantSettings]);

  const value = useMemo(
    () => ({
      tenant,
      tenantSettings,
      tenantId,
      tenantSlug,
      loading,
      error,
      isResolved,
      hasDedicatedDb,
      refreshTenant,
      isFeatureEnabled,
    }),
    [
      tenant,
      tenantSettings,
      tenantId,
      tenantSlug,
      loading,
      error,
      isResolved,
      hasDedicatedDb,
      refreshTenant,
      isFeatureEnabled,
    ]
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}
